import dynamic from 'next/dynamic';
import React from 'react';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import { DepartureSelectorProps } from '@/components/blocks/departure-selector/departure-selector';
import { DurationSelectorProps } from '@/components/blocks/duration-selector/duration-selector';
import { HotelSelectorProps } from '@/components/blocks/hotel-selector/hotel-selector';
import { ProductSelectorProps } from '@/components/blocks/product-selector/product-selector';
import { RoomMatrixContainerProps } from '@/components/blocks/room-matrix/room-matrix-container';
import { RoomMatrixSelectorProps } from '@/components/blocks/room-matrix-selector/room-matrix-selector';
import { TransportModeProps } from '@/components/blocks/transport-mode/transport-mode';
import { TransportsSelectorProps } from '@/components/blocks/transports-selector/transports-selector';
import styles from './configuration-flow-module-list.module.scss';

const ModuleLoadingFallback = () => (
  <SkeletonLoader
    className={styles.skeletonLoader}
    height="120px"
    width="100%"
  />
);

const DepartureSelector = dynamic(
  () => import('@/components/blocks/departure-selector/departure-selector'),
  { loading: ModuleLoadingFallback },
);
const DurationSelector = dynamic(
  () => import('@/components/blocks/duration-selector/duration-selector'),
  { loading: ModuleLoadingFallback },
);
const HotelSelector = dynamic(
  () => import('@/components/blocks/hotel-selector/hotel-selector'),
  { loading: ModuleLoadingFallback },
);
const ProductSelector = dynamic(
  () => import('@/components/blocks/product-selector/product-selector'),
  { loading: ModuleLoadingFallback },
);
const RoomMatrixContainer = dynamic(
  () => import('@/components/blocks/room-matrix/room-matrix-container'),
  { loading: ModuleLoadingFallback },
);
const RoomMatrixSelector = dynamic(
  () => import('@/components/blocks/room-matrix-selector/room-matrix-selector'),
  { loading: ModuleLoadingFallback },
);
const TransportMode = dynamic(
  () => import('@/components/blocks/transport-mode/transport-mode'),
  { loading: ModuleLoadingFallback },
);
const TransportsSelector = dynamic(
  () => import('@/components/blocks/transports-selector/transports-selector'),
  { loading: ModuleLoadingFallback },
);

export type ConfigurationFlowModule =
  | ({
      $type: 'DepartureSelectorModuleHeadlessDto';
    } & DepartureSelectorProps)
  | ({
      $type: 'DurationSelectorModuleDto';
    } & DurationSelectorProps)
  | ({
      $type: 'HotelSelectorModuleHeadlessDto';
    } & HotelSelectorProps)
  | ({
      $type: 'ProductSelectorModuleHeadlessDto';
    } & ProductSelectorProps)
  | ({
      $type: 'RoomMatrixHeadlessModuleDto';
    } & RoomMatrixContainerProps)
  | ({
      $type: 'RoomMatrixSelectorModuleHeadlessDto';
    } & RoomMatrixSelectorProps)
  | ({
      $type: 'TransportModeModuleDto';
    } & TransportModeProps)
  | ({
      $type: 'TransportsSelectorModuleHeadlessDto';
    } & TransportsSelectorProps);

const ConfigurationFlowModuleMap: {
  [key in ConfigurationFlowModule['$type']]: React.ComponentType<any>;
} = {
  DepartureSelectorModuleHeadlessDto: DepartureSelector,
  DurationSelectorModuleDto: DurationSelector,
  HotelSelectorModuleHeadlessDto: HotelSelector,
  ProductSelectorModuleHeadlessDto: ProductSelector,
  RoomMatrixHeadlessModuleDto: RoomMatrixContainer,
  RoomMatrixSelectorModuleHeadlessDto: RoomMatrixSelector,
  TransportModeModuleDto: TransportMode,
  TransportsSelectorModuleHeadlessDto: TransportsSelector,
};

function getConfigurationFlowModule(
  type: ConfigurationFlowModule['$type'],
  props: Omit<ConfigurationFlowModule, '$type'>,
): {
  component: React.ComponentType<Omit<ConfigurationFlowModule, '$type'>>;
  props: Omit<ConfigurationFlowModule, '$type'>;
} {
  return { component: ConfigurationFlowModuleMap[type], props };
}

export interface ConfigurationFlowModuleListProps {
  /** An array of the modules to show */
  contentModules: ConfigurationFlowModule[];
}

const ConfigurationFlowModuleList = ({
  contentModules = [],
}: ConfigurationFlowModuleListProps) => {
  return (
    <React.Fragment>
      {contentModules.map((module, moduleIndex) => {
        const { component: Component, props } = getConfigurationFlowModule(
          module.$type,
          module,
        );

        if (!Component) return null;

        return <Component key={moduleIndex} {...props} />;
      })}
    </React.Fragment>
  );
};

ConfigurationFlowModuleList.displayName = 'ConfigurationFlowModuleList';

export default ConfigurationFlowModuleList;
